import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import FirmasCierre from '../ui/FirmasCierre';
import { limpiarSerie, generarSerie } from '../../utils/serie';
import { toast } from 'react-hot-toast';
import { getTodayISO } from '../../utils/dateUtils';
import { useTheme } from '../../hooks/useTheme';
import { faltanFotos } from '../servicio/FotosEquipo';
import { subirFotosEquipo, fotosPorSubir, precargarFotosEquipo } from '../../utils/subirFoto';
import BotonProgreso from '../ui/BotonProgreso';
import { PreguntaPago, pagoCompleto } from '../servicio/ejecutar/PasoCobro';
import { buildSelectStyles } from '../servicio/ServicioUI';
import TicketItemsList from '../servicio/TicketItemsList';
import FormEquipoTicket from '../servicio/FormEquipoTicket';
import RepuestosBottomSheet from '../repuesto/RepuestosBottomSheet';
import { CausaReclamo } from './ReclamoEnFicha';
import { enviarOEncolar } from '../../utils/pendientesOffline';
import useBorradorCierre from '../../hooks/useBorradorCierre';

const inputCls = 'w-full px-3 py-2.5 rounded-xl bg-chip text-ink text-body font-medium outline-none focus:ring-2 focus:ring-[#D13A28]/40 placeholder:text-muted';
const labelCls = 'block text-label font-black text-muted uppercase tracking-wider mb-1';

const vacio = (serial = '') => ({
    trabajo: '', equipoSerial: serial, modeloEquipo: '', ubicacionEquipo: '', repuestosUsados: [],
    fotoAntes: null, fotoDespues: null, fotosExtra: [], fotosRotulos: null,
});
const resumenDe = (it) => [it.trabajo.trim(), ...(it.repuestosUsados || []).map(r => `${r.cantidad}x ${r.nombre}`)].filter(Boolean).join(' · ');
const fotosDe = (it) => ({ fotoAntes: it.fotoAntes, fotoDespues: it.fotoDespues, fotosExtra: it.fotosExtra || [], fotosRotulos: it.fotosRotulos || null });
const empezado = (it) => !!(it.trabajo.trim() || it.equipoSerial || it.repuestosUsados?.length || it.fotoAntes || it.fotoDespues || it.fotosExtra?.length);

/**
 * ModalRegistrarTrabajo — full-screen sheet del técnico.
 * Cerrar una visita sin presupuesto (`orden`) o sumar equipos a un trabajo ya cerrado hoy
 * (`servicio`, 21-oct-2026). Los equipos se cargan con la MISMA tarjeta y la misma lista
 * "equipos en el ticket" que usa el admin al editar un trabajo, sin precios: el precio lo pone
 * el admin (monto estimado de la visita); el técnico solo dice si le pagaron (4-oct-2026).
 */
export default function ModalRegistrarTrabajo({ orden, servicio = null, tecnicoId, onGuardado, onCerrar }) {
    const sumando = !!servicio;
    const tecnicoNombre = orden?.tecnicoNombre || '';
    const { isDark } = useTheme();
    const selectStyles = buildSelectStyles(isDark);
    const [repuestosDisp,  setRepuestosDisp]  = useState([]);
    const [sedes,          setSedes]          = useState([]);
    const [sedeId,         setSedeId]         = useState(servicio?.sedeId ? String(servicio.sedeId) : '');
    const [sedeNombre,     setSedeNombre]     = useState('');
    const [observaciones,  setObservaciones]  = useState('');
    const [firmas,         setFirmas]         = useState({ firmaTecnico: null, firmaCliente: null });
    const [pago,           setPago]           = useState(null);
    const [monto,          setMonto]          = useState(0);
    const precioAdmin = Number(orden?.montoEstimado) || 0;
    const [guardando,      setGuardando]      = useState(false);
    const [esMostrador,    setEsMostrador]    = useState(false);
    const [pedido,         setPedido]         = useState([]);   // equipos del pedido de empresa

    // Ticket: lo ya cargado (al sumar, bloqueado) + lo nuevo. El N/S de la visita viene cargado.
    const yaCargados = (servicio?.items || []).map(it => ({
        equipoSerial: ['MOSTRADOR', 'S/N', 'SIN-SN'].includes(String(it.equipoSerial || '').toUpperCase()) ? '' : it.equipoSerial, resumenTexto: it.trabajoRealizado || '', bloqueado: true,
    }));
    const [ticket,         setTicket]         = useState([]);
    const [itemActual,     setItemActual]     = useState(() => vacio(sumando ? '' : limpiarSerie(String(orden?.equiposSerie || '').split(',')[0])));
    const [formVisible,    setFormVisible]    = useState(true);
    const [mostrarEquipo,  setMostrarEquipo]  = useState(!!itemActual.equipoSerial);
    const [sheetRepuestos, setSheetRepuestos] = useState(false);
    const [generando,      setGenerando]      = useState(false);

    // Borrador en el celular (21-oct-2026): sin señal o si se cierra la app, no se pierde lo cargado
    const { recuperado, borrar: borrarBorrador } = useBorradorCierre(
        sumando ? `cierre_trabajo_${servicio.id}` : `cierre_orden_${orden.id}`,
        { ticket, itemActual, formVisible, pago, monto, observaciones, firmas, sedeId, sedeNombre },
        (b) => {
            if (Array.isArray(b.ticket)) setTicket(b.ticket);
            if (b.itemActual) { setItemActual({ ...vacio(), ...b.itemActual }); setMostrarEquipo(!!b.itemActual.equipoSerial); }
            if (typeof b.formVisible === 'boolean') setFormVisible(b.formVisible);
            if (b.pago) { setPago(b.pago); setMonto(b.monto || 0); }
            if (b.observaciones) setObservaciones(b.observaciones);
            if (b.firmas) setFirmas(b.firmas);
            if (b.sedeId) { setSedeId(b.sedeId); setSedeNombre(b.sedeNombre || ''); }
        },
        ticket.length > 0 || empezado(itemActual),
    );
    const empezarDeCero = () => {
        borrarBorrador();
        setTicket([]); setItemActual(vacio()); setFormVisible(true); setMostrarEquipo(false);
        setPago(null); setMonto(0); setObservaciones(''); setFirmas({ firmaTecnico: null, firmaCliente: null });
    };

    useEffect(() => {
        api.get('/repuestos', { params: { page: 0, size: 1000 } })
            .then(r => { const d = r.data; setRepuestosDisp(Array.isArray(d) ? d : (d?.content || [])); })
            .catch(() => {});
        // Sedes del cliente de la visita. Si no tiene (venta o cliente sin dirección
        // cargada) se usa la sede "Mostrador" (5-oct-2026).
        const normalizar = d => (Array.isArray(d) ? d : (d?.content || []))
            .map(s => ({ ...s, nombre: s.nombre || s.nombreSede || s.direccion || `Sede ${s.id}` }));
        const elegir = async (lista) => {
            setSedes(lista);
            if (sumando) return;   // el lugar es el del trabajo
            // Pedido de empresa: el lugar ya viene elegido (18-oct-2026)
            let delPedido = null;
            if (orden.id) {
                try {
                    const r = await api.get(`/ordenes/${orden.id}/equipos-pedido`);
                    delPedido = lista.find(x => x.id === r.data?.sedeId) || null;
                    setPedido(r.data?.equipos || []);
                } catch { /* se elige a mano */ }
            }
            const s = lista.length === 1 ? lista[0] : delPedido;
            if (s) {
                setSedeId(String(s.id));
                setSedeNombre(s.nombre || s.descripcion || '');
            }
        };
        const mostrador = () => api.get('/sedes/mostrador')
            .then(r => { const l = normalizar(r.data); setEsMostrador(l.length > 0); elegir(l); })
            .catch(() => {});
        if (!orden?.clienteId) { if (!sumando) mostrador(); return; }
        api.get(`/sedes/cliente/${orden.clienteId}`)
            .then(r => { const l = normalizar(r.data); if (l.length) elegir(l); else if (!sumando) mostrador(); })
            .catch(() => { if (!sumando) mostrador(); });
    }, [orden?.clienteId, orden?.id, sumando]);

    const sedeSel = sedes.find(s => String(s.id) === String(sedeId));
    const enTicket = new Set([...yaCargados, ...ticket].map(i => limpiarSerie(i.equipoSerial)).filter(Boolean));

    // Opciones del N/S: los equipos del lugar, los del pedido y los de la visita, sin los que ya están
    const opcionesSerial = (() => {
        const vistos = new Set();
        const out = [];
        const sumar = (serie, label) => {
            const s = limpiarSerie(serie);
            if (!s || vistos.has(s) || enTicket.has(s)) return;
            vistos.add(s);
            out.push({ value: s, label });
        };
        (sedeSel?.equipos || []).forEach(e => sumar(e.numeroSerie, `S/N: ${e.numeroSerie}${e.ubicacion || e.modelo ? ` — ${e.ubicacion || e.modelo}` : ''}`));
        pedido.filter(e => e.serie).forEach(e => sumar(e.serie, `S/N: ${e.serie} (del pedido)`));
        String(orden?.equiposSerie || '').split(',').forEach(s => sumar(s, `S/N: ${limpiarSerie(s)} (de la visita)`));
        return out;
    })();
    const pedidoSinSerie = pedido.filter(e => !e.serie).length;

    const elegirSerie = (s) => {
        if (!s) { setItemActual(it => ({ ...it, equipoSerial: '' })); return; }
        const eq = (sedeSel?.equipos || []).find(e => limpiarSerie(e.numeroSerie) === s.value);
        setItemActual(it => ({ ...it, equipoSerial: s.value,
            modeloEquipo: it.modeloEquipo || eq?.modelo || '', ubicacionEquipo: it.ubicacionEquipo || eq?.ubicacion || '' }));
    };
    const generarPara = async () => {
        setGenerando(true);
        try {
            const s = await generarSerie(tecnicoNombre, [...enTicket]);
            setItemActual(it => ({ ...it, equipoSerial: s, nsGenerado: true }));
            toast.success(`N/S generado: ${s}`);
        } catch { toast.error('No se pudo generar el N/S'); }
        finally { setGenerando(false); }
    };

    // Validar el equipo que se está cargando; devuelve un error o null
    const errorDe = (it) => {
        if (!it.trabajo.trim()) return `Describí el trabajo del equipo ${yaCargados.length + ticket.length + 1}`;
        const s = limpiarSerie(it.equipoSerial);
        if (s && enTicket.has(s)) return `El equipo ${s} ya está en el ticket`;
        const f = faltanFotos(fotosDe(it));
        if (f.length) return `Falta la ${f.join(' y la ')}`;
        return null;
    };
    const agregarAlTicket = () => {
        const err = errorDe(itemActual);
        if (err) { toast.error(err); return; }
        setTicket(t => [...t, { ...itemActual, equipoSerial: limpiarSerie(itemActual.equipoSerial), resumenTexto: resumenDe(itemActual) }]);
        setItemActual(vacio());
        setMostrarEquipo(false);
        setFormVisible(false);
    };
    const offset = yaCargados.length;
    const editarItem = (idx) => {
        const i = idx - offset;
        if (i < 0) return;
        if (empezado(itemActual)) { toast.error('Primero agregá o borrá el equipo que estás cargando'); return; }
        setItemActual(ticket[i]);
        setMostrarEquipo(!!ticket[i].equipoSerial);
        setTicket(t => t.filter((_, k) => k !== i));
        setFormVisible(true);
    };
    const eliminarItem = (idx) => {
        const i = idx - offset;
        if (i >= 0) setTicket(t => t.filter((_, k) => k !== i));
    };

    // Lo que se guarda: el ticket + el equipo a medio cargar si ya tiene el trabajo escrito
    const pendienteListo = formVisible && itemActual.trabajo.trim();
    const aGuardar = pendienteListo ? [...ticket, itemActual] : ticket;
    const pendienteIncompleto = formVisible && !itemActual.trabajo.trim() && empezado(itemActual);
    const puedeGuardar = aGuardar.length > 0 && !pendienteIncompleto && (sumando || (sedeId && pagoCompleto(pago, monto)));

    // 27-oct-2026: las fotos se empiezan a subir apenas se sacan, mientras se completa el resto;
    // al cerrar se reusan (las que ya subieron no se vuelven a mandar). Igual que en la carga por N/S.
    const firmaFotos = [...ticket, itemActual].map(it => [it.fotoAntes, it.fotoDespues, ...(it.fotosExtra || [])].map(f => (f ? String(f).length : 0)).join('.')).join('|');
    useEffect(() => {
        [...ticket, itemActual].forEach(it => precargarFotosEquipo(fotosDe(it)));
    }, [firmaFotos]); // eslint-disable-line react-hooks/exhaustive-deps
    const [progreso, setProgreso] = useState(null);

    const [causa, setCausa] = useState(orden?.reclamo?.causa || null);   // reclamo: por qué volvió a fallar (28-oct-2026)
    const handleGuardar = async () => {
        if (orden?.reclamo && !causa) { toast.error('Elegí por qué volvió a fallar'); return; }
        if (orden?.reclamo && causa !== orden.reclamo.causa) {
            try { await api.patch(`/ordenes/${orden.id}/reclamo`, { causa }); }
            catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo guardar la causa'); return; }
        }
        if (!sumando && !sedeId) { toast.error('Selecciona la sede'); return; }
        if (pendienteIncompleto) { toast.error(`Describí el trabajo del equipo ${offset + ticket.length + 1} o borralo`); return; }
        if (pendienteListo) { const err = errorDe(itemActual); if (err) { toast.error(err); return; } }
        if (!aGuardar.length) { toast.error('Cargá al menos un equipo'); return; }
        if (!sumando && !pagoCompleto(pago, monto)) { toast.error('Decinos si te pagó'); return; }
        if (!sumando && firmas.firmaCliente && !(firmas.aclaracionCliente || '').trim()) { toast.error('Poné la aclaración de quién firma'); return; }

        setGuardando(true);
        const totalFotos = fotosPorSubir(aGuardar.map(fotosDe));
        let hechas = 0;
        const pasoFotos = () => setProgreso({ texto: `Subiendo fotos ${hechas}/${totalFotos}`, pct: 8 + (hechas / totalFotos) * 62 });
        if (totalFotos > 0) pasoFotos(); else setProgreso({ texto: 'Guardando el trabajo…', pct: 40 });
        try {
            // Fotos de cada equipo en paralelo (las que ya se subieron en segundo plano se reusan)
            const subidas = await Promise.all(aGuardar.map((it, i) => subirFotosEquipo(fotosDe(it), `orden${orden?.id || servicio?.id}-${offset + i + 1}`, () => { hechas++; pasoFotos(); })));
            const fallaron = subidas.reduce((a, x) => a + (x.fallaron || 0), 0);
            // Sin señal las fotos no suben: no se cierra sin ellas. Queda todo guardado en el celular
            // (fotos incluidas) para cerrar cuando vuelva la señal, o sacar esas fotos y cerrar ya.
            if (fallaron) {
                toast(`Sin señal: ${fallaron} foto${fallaron !== 1 ? 's' : ''} no se pudo subir. Quedó todo guardado en el celular: tocá de nuevo cuando vuelva la señal, o sacá esas fotos para cerrar sin ellas.`, { icon: '📶', duration: 9000 });
                return;
            }

            setProgreso({ texto: sumando ? 'Sumando los equipos…' : 'Guardando el trabajo…', pct: 75 });
            const efectivo = pago === 'EFECTIVO';
            // Si no hay precio cargado y le pagaron, lo que cobró es el precio
            const precio = precioAdmin || (pago !== 'NO' ? Number(monto) : 0);
            const items = aGuardar.map((it, i) => {
                const { fallaron: _f, ...fotosSubidas } = subidas[i];
                return {
                    equipoSerial:     limpiarSerie(it.equipoSerial) || 'S/N',
                    tecnico:          tecnicoNombre || String(tecnicoId),
                    trabajoTipo:      'REPARACION',
                    metodoPago:       pago === 'TRANSFERENCIA' ? 'TRANSFERENCIA' : 'EFECTIVO',
                    trabajoRealizado: it.trabajo.trim(),
                    // El precio de la visita va en el primer equipo (es uno solo para toda la visita)
                    costo:            i === 0 ? precio : 0,
                    // Un N/S nuevo se da de alta al guardar, en el lugar del trabajo, con su modelo y ubicación
                    modeloEquipo:     it.modeloEquipo || null,
                    ubicacionEquipo:  it.ubicacionEquipo || null,
                    repuestosUsados:  (it.repuestosUsados || []).map(r => ({ id: r.id, nombre: r.nombre, cantidad: r.cantidad, precio: r.precio || 0 })),
                    ...fotosSubidas,
                };
            });

            if (sumando) {
                try {
                    const r = await enviarOEncolar('post', `/servicios/${servicio.id}/sumar-equipos`, { items }, `Sumar equipos #${servicio.id}`);
                    borrarBorrador();
                    if (r.encolado) toast('Sin señal: los equipos quedaron guardados en el celular y se mandan solos', { icon: '📶', duration: 6000 });
                    else toast.success(items.length > 1 ? `${items.length} equipos sumados` : 'Equipo sumado');
                    onGuardado();
                } catch (e) {
                    toast.error(e?.response?.data?.mensaje || 'No se pudieron sumar los equipos. Probá de nuevo.');
                }
                return;
            }

            // Dos llamadas separadas a proposito (crear el servicio, despues cerrar la
            // orden): si la primera falla no se creo nada, se puede reintentar tranquilo.
            // Si la segunda falla, el servicio YA existe: se avisa y se cierra el modal
            // para que no se reintente y se duplique el trabajo.
            const notaTransf = pago === 'TRANSFERENCIA' ? `Pagó por transferencia $${Math.round(monto).toLocaleString('es-AR')} — verificar` : '';
            const obsFinal = [observaciones.trim(), notaTransf].filter(Boolean).join('\n') || null;
            let encolado = false;
            try {
                const r = await enviarOEncolar('post', '/servicios', {
                    clienteNombre: orden.clienteNombre || '',
                    sedeId:        Number(sedeId),
                    sedeNombre:    sedeNombre || sedeSel?.nombre || '',
                    usuarioId:     tecnicoId,
                    servicioTipo:  'TECNICA',
                    estado:        efectivo ? 'COBRADO' : 'COMPLETADO',
                    ...(efectivo ? { modalidadCobro: 'EFECTIVO_SIN_FACTURA', montoFinal: Number(monto) } : {}),
                    fecha:         getTodayISO(),
                    ordenId:       orden.id,
                    // La visita se cierra en el mismo pedido que guarda el trabajo (23-oct-2026):
                    // el PATCH de abajo queda de respaldo y no repite nada si ya está cerrada
                    cerrarVisita:  true,
                    notasVisita:   (items.length === 1 ? items[0].trabajoRealizado : items.map(it => `${it.equipoSerial}: ${it.trabajoRealizado}`).join(' · '))
                        + (observaciones.trim() ? ` | Obs: ${observaciones.trim()}` : ''),
                    observaciones: obsFinal,
                    firmaCliente: firmas.firmaCliente || null, firmaTecnico: firmas.firmaTecnico || null, aclaracionCliente: firmas.firmaCliente ? (firmas.aclaracionCliente || '').trim() || null : null,
                    items,
                }, `Trabajo ${orden.clienteNombre || ''} (visita ${orden.id})`);
                encolado = r.encolado;
            } catch (e) {
                toast.error(e?.response?.data?.mensaje || 'No se pudo registrar el trabajo. Probá de nuevo.');
                return;
            }
            // El trabajo ya quedó (o está en la cola, que respeta el orden): el borrador no hace falta
            borrarBorrador();
            setProgreso({ texto: 'Cerrando la visita…', pct: 92 });

            try {
                const resumen = items.length === 1 ? items[0].trabajoRealizado
                    : items.map(it => `${it.equipoSerial}: ${it.trabajoRealizado}`).join(' · ');
                const nota = observaciones.trim() ? `${resumen} | Obs: ${observaciones.trim()}` : resumen;
                const r = await enviarOEncolar('patch', `/ordenes/${orden.id}/estado`, { estado: 'COMPLETADA', notasTecnico: nota.slice(0, 1000) }, `Cerrar visita ${orden.clienteNombre || ''}`);
                if (encolado || r.encolado) toast('Sin señal: el trabajo quedó guardado en el celular y se manda solo cuando vuelva', { icon: '📶', duration: 7000 });
                else toast.success(items.length > 1 ? `Trabajo registrado · ${items.length} equipos` : 'Trabajo registrado');
                encolado = encolado || r.encolado;
            } catch {
                toast.error('El trabajo se guardó, pero no se pudo cerrar la orden. Avisale al admin para que la cierre — no lo vuelvas a cargar.', { duration: 8000 });
            }
            setProgreso({ texto: 'Listo', pct: 100 });
            onGuardado({ encolado });
        } finally {
            setGuardando(false);
            setProgreso(null);
        }
    };

    const listaTicket = [...yaCargados, ...ticket];
    const nuevos = aGuardar.length;

    return (
        <div className="fixed inset-0 z-[3000] flex flex-col bg-page">
            {/* Header */}
            <div className="shrink-0 px-4 pt-4 pb-3 bg-panel border-b border-black/[0.08]">
                <div className="flex items-center gap-3">
                    <button onClick={onCerrar}
                        className="w-11 h-11 rounded-xl flex items-center justify-center font-bold bg-chip text-secondary active:scale-90">
                        ←
                    </button>
                    <div className="flex-1 min-w-0">
                        <h2 className="text-title font-black text-ink leading-none">{sumando ? 'Sumar equipos' : 'Cerrar trabajo'}</h2>
                        <p className="text-caption text-muted truncate mt-0.5">{orden?.clienteNombre || servicio?.clienteNombre || 'Cliente'}{sumando ? ` · #${servicio.id}` : ` · ${orden.titulo}`}</p>
                    </div>
                </div>
            </div>

            {/* Contenido scrollable */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 pb-32">
                {recuperado && (
                    <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-chip">
                        <p className="flex-1 text-caption font-bold text-ink">Seguís con lo que tenías cargado.</p>
                        <button type="button" onClick={empezarDeCero} className="shrink-0 h-9 px-3 rounded-lg text-label font-black text-brand-red active:scale-95">Empezar de cero</button>
                    </div>
                )}
                {!sumando && orden?.reclamo && (
                    <div className="p-3 rounded-2xl bg-panel"><CausaReclamo valor={causa} onElegir={setCausa} /></div>
                )}
                {/* Sede */}
                {!sumando && sedes.length > 1 && (
                    <div>
                        <label className={labelCls}>Sede *</label>
                        <select value={sedeId} onChange={e => {
                            setSedeId(e.target.value);
                            const s = sedes.find(x => String(x.id) === e.target.value);
                            setSedeNombre(s?.nombre || s?.descripcion || '');
                        }} className={inputCls}>
                            <option value="">Seleccionar sede...</option>
                            {sedes.map(s => (
                                <option key={s.id} value={s.id}>{s.nombre || s.descripcion}</option>
                            ))}
                        </select>
                    </div>
                )}
                {!sumando && sedes.length === 0 && (
                    <div>
                        <label className={labelCls}>Sede *</label>
                        <p className="text-caption text-[#D13A28] font-bold">Este cliente no tiene dirección cargada. Avisale al admin.</p>
                    </div>
                )}
                {!sumando && esMostrador && sedes.length > 0 && (
                    <p className="text-caption text-muted">El cliente no tiene dirección cargada: se registra como mostrador.</p>
                )}

                {/* Equipos: misma lista y misma tarjeta que el admin (21-oct-2026) */}
                <TicketItemsList ticketItems={listaTicket} editarItem={editarItem} eliminarItem={eliminarItem} sinPrecio
                    titulo={sumando && listaTicket.length ? `${listaTicket.length} equipo${listaTicket.length > 1 ? 's' : ''} en el trabajo` : null} />

                {pedidoSinSerie > 0 && (
                    <p className="text-caption text-muted">El pedido tiene {pedidoSinSerie} equipo{pedidoSinSerie > 1 ? 's' : ''} sin N/S: generale uno a cada uno con «Generar N/S automático».</p>
                )}

                {formVisible ? (
                    <FormEquipoTicket numero={listaTicket.length + 1}
                        itemActual={itemActual} setItemActual={setItemActual} selectStyles={selectStyles}
                        opcionesSerial={opcionesSerial} onElegirSerie={elegirSerie}
                        onCrearSerie={serie => setItemActual(it => ({ ...it, equipoSerial: serie }))}
                        onGenerarSerie={generarPara} generandoSerie={generando}
                        mostrarEquipo={mostrarEquipo} setMostrarEquipo={setMostrarEquipo}
                        onAbrirRepuestos={() => setSheetRepuestos(true)}
                        autoFocus={listaTicket.length > 0}
                        anterior={ticket.length ? ticket[ticket.length - 1] : null}
                        onAgregar={agregarAlTicket} />
                ) : (
                    <button type="button" onClick={() => setFormVisible(true)}
                        className="w-full py-3.5 rounded-2xl font-black text-label uppercase border-2 border-dashed border-[#D13A28]/40 dark:border-[#E8422F]/40 text-brand-red bg-transparent active:scale-[0.98] transition-all">
                        + Agregar equipo
                    </button>
                )}

                {!sumando && (
                    <>
                        {/* ¿Te pagó? — mismo bloque que al cerrar un presupuesto */}
                        <div className="space-y-2">
                            <label className={labelCls}>¿Te pagó? *{precioAdmin > 0 && <span className="normal-case tracking-normal font-bold"> · Precio: ${Math.round(precioAdmin).toLocaleString('es-AR')}</span>}</label>
                            <PreguntaPago total={precioAdmin} pago={pago} setPago={setPago} monto={monto} setMonto={setMonto} />
                        </div>

                        {/* Observaciones para el admin */}
                        <div>
                            <label className={labelCls}>Observaciones (opcional)</label>
                            <textarea value={observaciones} onChange={e => setObservaciones(e.target.value)}
                                rows={2} placeholder="Notas para el admin: estado del equipo, recomendaciones, problemas..."
                                className={`${inputCls} resize-none`} />
                        </div>

                        {/* Firmas (16-oct-2026): quedan en el trabajo y salen en el PDF */}
                        <div>
                            <label className={labelCls}>Firmas</label>
                            <FirmasCierre value={firmas} onChange={setFirmas} />
                        </div>
                    </>
                )}
            </div>

            {/* Boton fijo abajo */}
            <div className="shrink-0 flex gap-2 px-4 py-4 bg-panel border-t border-black/[0.08]">
                <button onClick={onCerrar} disabled={guardando}
                    className="flex-1 py-3 rounded-2xl font-black text-label uppercase bg-chip text-secondary active:scale-95 transition-all disabled:opacity-40">
                    Cancelar
                </button>
                {guardando ? (
                    <div className="flex-[2] min-w-0"><BotonProgreso progreso={progreso} className="h-12 rounded-2xl" aviso={false} /></div>
                ) : <button onClick={handleGuardar} disabled={!puedeGuardar}
                    className="flex-[2] py-3 rounded-2xl font-black text-label uppercase text-white active:scale-95 transition-all bg-brand-red disabled:opacity-40">
                    {sumando ? `Sumar ${nuevos || ''} equipo${nuevos === 1 ? '' : 's'}`.replace('  ', ' ')
                        : nuevos > 1 ? `Cerrar · ${nuevos} equipos` : 'Cerrar trabajo'}
                </button>}
            </div>

            <RepuestosBottomSheet isOpen={sheetRepuestos} onClose={() => setSheetRepuestos(false)}
                repuestos={repuestosDisp} seleccionados={itemActual.repuestosUsados || []}
                onChange={n => setItemActual(it => ({ ...it, repuestosUsados: n }))} />
        </div>
    );
}
